import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ApiServices from '../Services/ApiServices';
import { toastHelper } from '../../Helpers/toastHelper';
import Loader from '../Shared/Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faLayerGroup,
    faTags,
    faClock,
    faCrown,
    faUsers,
    faDatabase,
    faSave,
    faArrowRight,
    faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const AddPlan = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { plan: editPlan } = location.state || {};
    const isEdit = !!editPlan;

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [periods, setPeriods] = useState([]);

    const fetchPeriods = async () => {
        try {
            const res = await ApiServices.get('Lookup/PlansPeriod');
            if (res) setPeriods(res);
        } catch (error) {
            toastHelper.error('فشل في تحميل الفترات');
        }
    };

    useEffect(() => {
        fetchPeriods();
    }, []);

    const formik = useFormik({
        initialValues: {
            name: editPlan?.name || '',
            description: editPlan?.description || '',
            original_price: editPlan?.original_price || '',
            discount: editPlan?.discount || '',
            period: editPlan?.period || '',
            is_vip: editPlan?.is_vip || false,
            max_users: editPlan?.max_users || 0,
            max_storage: editPlan?.max_storage || 0,
        },
        enableReinitialize: true,
        validationSchema: Yup.object({
            name: Yup.string().required('يرجى إدخال اسم الخطة'),
            description: Yup.string().required('يرجى إدخال وصف الخطة').min(10, 'الوصف قصير جداً'),
            original_price: Yup.number().required('يرجى إدخال السعر'),
            period: Yup.number().required('يرجى اختيار الفترة'),
            max_users: Yup.number().required('يرجى إدخال الحد الأقصى للطلاب'),
            max_storage: Yup.number().required('يرجى إدخال الحد الأقصى للمساحة (MB)')
        }),
        onSubmit: async (values) => {
            setSubmitting(true);
            try {
                const payload = {
                    ...values,
                    original_price: parseFloat(values.original_price),
                    discount: values.discount ? parseFloat(values.discount) : 0,
                    max_users: parseInt(values.max_users),
                    max_storage: parseInt(values.max_storage)
                };

                const response = isEdit
                    ? await ApiServices.put(`Subscription/Plan/update/${editPlan.id}`, payload)
                    : await ApiServices.post('Subscription/Plan/create', payload);

                if (response && response.check) {
                    toastHelper.success(isEdit ? 'تم تحديث الخطة بنجاح' : 'تم إنشاء الخطة بنجاح');
                    navigate('/admin/dashboard');
                } else {
                    toastHelper.error(response.message || 'فشل في العملية');
                }
            } catch (error) {
                toastHelper.error('حدث خطأ أثناء الاتصال بالسيرفر');
            } finally {
                setSubmitting(false);
            }
        }
    });

    const customSelectStyles = {
        container: (base) => ({ ...base, width: '100%' }),
        control: (base, state) => ({
            ...base,
            padding: '6px',
            borderRadius: '12px',
            border: state.isFocused ? '2px solid #6366f1' : '2px solid #e2e8f0',
            fontFamily: 'Zain, sans-serif'
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#6366f1' : state.isFocused ? '#f1f5f9' : 'white',
            color: state.isSelected ? 'white' : '#334155',
            fontFamily: 'Zain, sans-serif'
        }),
        singleValue: (base) => ({ ...base, fontFamily: 'Zain, sans-serif' })
    };

    if (loading) return <Loader text="جاري التحميل..." />;

    return (
        <div className="add-plan-page">
            <div className="form-container">
                <header className="form-header">
                    <button className="back-btn" onClick={() => navigate(-1)}>
                        <FontAwesomeIcon icon={faArrowRight} />
                        <span>عودة</span>
                    </button>
                    <h1>{isEdit ? 'تعديل خطة اشتراك' : 'إضافة خطة اشتراك جديدة'}</h1>
                </header>

                <form onSubmit={formik.handleSubmit}>
                    <div className="form-grid">
                        <div className="input-group full-width">
                            <label><FontAwesomeIcon icon={faLayerGroup} /> اسم الخطة</label>
                            <input
                                {...formik.getFieldProps('name')}
                                placeholder="مثلاً: الخطة الاحترافية"
                                className={formik.touched.name && formik.errors.name ? 'input-error' : ''}
                            />
                            {formik.touched.name && formik.errors.name && <div className="field-error">{formik.errors.name}</div>}
                        </div>

                        <div className="input-group full-width">
                            <label><FontAwesomeIcon icon={faInfoCircle} /> وصف الخطة</label>
                            <textarea
                                {...formik.getFieldProps('description')}
                                placeholder="اكتب وصفاً مفصلاً عما تقدمه هذه الخطة..."
                                rows="3"
                                className={formik.touched.description && formik.errors.description ? 'input-error' : ''}
                            />
                            {formik.touched.description && formik.errors.description && <div className="field-error">{formik.errors.description}</div>}
                        </div>

                        <div className="input-group">
                            <label><FontAwesomeIcon icon={faTags} /> السعر الأصلي (EGP)</label>
                            <input
                                type="number"
                                {...formik.getFieldProps('original_price')}
                                placeholder="السعر قبل الخصم"
                            />
                            {formik.touched.original_price && formik.errors.original_price && <div className="field-error">{formik.errors.original_price}</div>}
                        </div>

                        <div className="input-group">
                            <label><FontAwesomeIcon icon={faTags} /> الخصم (EGP - اختياري)</label>
                            <input
                                type="number"
                                {...formik.getFieldProps('discount')}
                                placeholder="قيمة الخصم إن وجدت"
                            />
                        </div>

                        <div className="input-group">
                            <label><FontAwesomeIcon icon={faClock} /> مدة الخطة</label>
                            <Select
                                options={periods.map(p => ({ value: p.id, label: p.name }))}
                                value={periods.map(p => ({ value: p.id, label: p.name })).find(o => o.value === formik.values.period)}
                                isRtl={true}
                                placeholder="اختر المدة..."
                                styles={customSelectStyles}
                                onChange={(opt) => formik.setFieldValue('period', opt ? opt.value : '')}
                            />
                            {formik.touched.period && formik.errors.period && <div className="field-error">{formik.errors.period}</div>}
                        </div>

                        <div className="input-group">
                            <label className="checkbox-label">
                                <span className={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FontAwesomeIcon icon={faCrown} style={{ color: '#6366f1' }} />
                                    خطة VIP؟
                                </span>
                                <input
                                    type="checkbox"
                                    className="custom-checkbox"
                                    checked={formik.values.is_vip}
                                    onChange={(e) => formik.setFieldValue('is_vip', e.target.checked)}
                                />
                            </label>
                        </div>

                        <div className="input-group">
                            <label><FontAwesomeIcon icon={faUsers} /> أقصى عدد للطلاب</label>
                            <input
                                type="number"
                                {...formik.getFieldProps('max_users')}
                                placeholder="0 تعني غير محدود"
                            />
                        </div>

                        <div className="input-group">
                            <label><FontAwesomeIcon icon={faDatabase} /> أقصى مساحة تخزين (MB)</label>
                            <input
                                type="number"
                                {...formik.getFieldProps('max_storage')}
                                placeholder="0 تعني غير محدود"
                            />
                        </div>
                    </div>

                    <footer className="form-footer">
                        <button type="submit" className="submit-btn" disabled={submitting}>
                            <FontAwesomeIcon icon={faSave} spin={submitting} />
                            <span>{submitting ? 'جاري الحفظ...' : (isEdit ? 'تحديث الخطة' : 'إنشاء الخطة')}</span>
                        </button>
                    </footer>
                </form>
            </div>

            <style>{`
                .add-plan-page { 
                    padding: 40px 20px; 
                    background: #f8fafc; 
                    min-height: 100vh; 
                    direction: rtl; 
                      font-family: "Zain", sans-serif; 
                }
                .form-container { 
                    max-width: 900px; 
                    margin: 0 auto; 
                    background: white; 
                    padding: 40px; 
                    border-radius: 24px; 
                    box-shadow: 0 10px 40px rgba(0,0,0,0.05); 
                }
                .form-header { 
                    display: flex; 
                    align-items: center; 
                    gap: 20px; 
                    margin-bottom: 40px; 
                    border-bottom: 2px solid #f1f5f9; 
                    padding-bottom: 20px; 
                }
                .back-btn { 
                    background: #f1f5f9; 
                    border: none; 
                    padding: 10px 20px; 
                    border-radius: 12px; 
                    cursor: pointer; 
                    color: #64748b; 
                    font-weight: 700; 
                    display: flex; 
                    align-items: center; 
                    gap: 10px; 
                    transition: all 0.2s;
                }
                .back-btn:hover {
                    background: #e2e8f0;
                    color: #1e293b;
                }
                .form-header h1 { 
                    margin: 0; 
                    font-size: 1.8rem; 
                    color: #1e293b; 
                    font-weight: 800; 
                }
                .form-grid { 
                    display: grid; 
                    grid-template-columns: 1fr 1fr; 
                    gap: 30px; 
                }
                .full-width { 
                    grid-column: 1 / -1; 
                }
                
                .input-group label { 
                    display: flex; 
                    align-items: center; 
                    gap: 10px; 
                    margin-bottom: 12px; 
                    font-weight: 700; 
                    color: #475569; 
                    font-size: 0.95rem; 
                }
                .input-group input, 
                .input-group textarea { 
                    width: 100%; 
                    padding: 14px 18px; 
                    border: 2px solid #e2e8f0; 
                    border-radius: 12px; 
                    font-size: 1rem; 
                    transition: all 0.2s; 
                      font-family: "Zain", sans-serif; 
                    display: block; 
                    background: #fcfcfc;
                }
                .input-group input:focus, 
                .input-group textarea:focus { 
                    border-color: #6366f1; 
                    outline: none; 
                    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1); 
                    background: white;
                }
                
                .checkbox-label { 
                    display: flex;
                    align-items: center;
                    justify-content: space-between; 
                    padding: 14px 18px; 
                    background: #f8fafc; 
                    border-radius: 12px; 
                    border: 2px solid #e2e8f0; 
                    cursor: pointer; 
                    transition: all 0.2s;
                    height: 100%;
                }
                .checkbox-label:hover {
                    border-color: #6366f1;
                    background: #f0f7ff;
                }
                .label-content {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-weight: 700;
                    color: #475569;
                }
                .custom-checkbox { 
                    width: 20px !important; 
                    height: 20px !important; 
                    cursor: pointer; 
                    accent-color: #6366f1;
                }
                
                .field-error { 
                    color: #ef4444; 
                    font-size: 0.85rem; 
                    margin-top: 8px; 
                    font-weight: 700; 
                }
                .input-error { 
                    border-color: #ef4444 !important; 
                }

                .form-footer { 
                    margin-top: 40px; 
                    padding-top: 30px; 
                    border-top: 2px solid #f1f5f9; 
                    display: flex; 
                    justify-content: center; 
                }
                .submit-btn { 
                    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%); 
                    color: white; 
                    border: none; 
                    padding: 16px 60px; 
                    border-radius: 15px; 
                    font-size: 1.1rem; 
                    font-weight: 700; 
                    cursor: pointer; 
                    display: flex; 
                    align-items: center; 
                    gap: 12px; 
                    transition: all 0.3s; 
                    box-shadow: 0 10px 20px -5px rgba(99, 102, 241, 0.4); 
                }
                .submit-btn:hover:not(:disabled) { 
                    transform: translateY(-2px); 
                    box-shadow: 0 15px 30px -5px rgba(99, 102, 241, 0.5); 
                }
                .submit-btn:disabled { 
                    opacity: 0.7; 
                    cursor: not-allowed; 
                }

                @media (max-width: 768px) {
                    .form-grid {
                        grid-template-columns: 1fr;
                        gap: 20px;
                    }
                    .form-container {
                        padding: 25px;
                    }
                    .submit-btn {
                        width: 100%;
                    }
                    .form-header h1 {
                        font-size: 1.4rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default AddPlan;
